import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import { Application, ApplicationStatus } from '../types';
import { applicationService, ApplicationFilters as ServiceFilters } from '../services/application.service';
import { useNotificationContext } from './NotificationContext';

// ApplicationContext용 필터 인터페이스 (확장)
export interface ApplicationFilters {
  status?: ApplicationStatus[];
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  hospitalId?: string;
  departmentId?: string;
}

// 통계 인터페이스
export interface ApplicationStats {
  total: number;
  byStatus: Record<ApplicationStatus, number>;
  recentActivity: number;
  avgProcessingTime: number;
  successRate: number;
}

// 상태 인터페이스
interface ApplicationState {
  applications: Application[];
  selectedIds: string[];
  filters: ApplicationFilters;
  statistics: ApplicationStats | null;
  loading: boolean;
  error: string | null;
  bulkActionLoading: boolean;
  socketConnected: boolean;
}

// 액션 타입
type ApplicationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_BULK_ACTION_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_APPLICATIONS'; payload: Application[] }
  | { type: 'UPDATE_APPLICATION'; payload: { id: string; updates: Partial<Application> } }
  | { type: 'ADD_APPLICATION'; payload: Application }
  | { type: 'REMOVE_APPLICATION'; payload: string }
  | { type: 'SET_SELECTED_IDS'; payload: string[] }
  | { type: 'TOGGLE_SELECTED_ID'; payload: string }
  | { type: 'CLEAR_SELECTED_IDS' }
  | { type: 'SET_FILTERS'; payload: ApplicationFilters }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'SET_STATISTICS'; payload: ApplicationStats }
  | { type: 'SET_SOCKET_CONNECTED'; payload: boolean };

// 초기 상태
const initialState: ApplicationState = {
  applications: [],
  selectedIds: [],
  filters: {},
  statistics: null,
  loading: false,
  error: null,
  bulkActionLoading: false,
  socketConnected: false
};

// 리듀서
const applicationReducer = (state: ApplicationState, action: ApplicationAction): ApplicationState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_BULK_ACTION_LOADING':
      return { ...state, bulkActionLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_APPLICATIONS':
      return { ...state, applications: action.payload, loading: false, error: null };
    
    case 'UPDATE_APPLICATION':
      return {
        ...state,
        applications: (state.applications || []).map(app =>
          app.id === action.payload.id
            ? { ...app, ...action.payload.updates }
            : app
        )
      };
    
    case 'ADD_APPLICATION':
      return {
        ...state,
        applications: [action.payload, ...(state.applications || [])]
      };
    
    case 'REMOVE_APPLICATION':
      return {
        ...state,
        applications: (state.applications || []).filter(app => app.id !== action.payload),
        selectedIds: state.selectedIds.filter(id => id !== action.payload)
      };
    
    case 'SET_SELECTED_IDS':
      return { ...state, selectedIds: action.payload };
    
    case 'TOGGLE_SELECTED_ID':
      return {
        ...state,
        selectedIds: state.selectedIds.includes(action.payload)
          ? state.selectedIds.filter(id => id !== action.payload)
          : [...state.selectedIds, action.payload]
      };
    
    case 'CLEAR_SELECTED_IDS':
      return { ...state, selectedIds: [] };
    
    case 'SET_FILTERS':
      return { ...state, filters: action.payload };
    
    case 'CLEAR_FILTERS':
      return { ...state, filters: {} };
    
    case 'SET_STATISTICS':
      return { ...state, statistics: action.payload };
    
    case 'SET_SOCKET_CONNECTED':
      return { ...state, socketConnected: action.payload };
    
    default:
      return state;
  }
};

// 필터 변환 함수 (ApplicationFilters -> ServiceFilters)
const convertFiltersForService = (filters: ApplicationFilters): ServiceFilters => {
  return {
    status: filters.status?.[0] || 'all', // 첫 번째 상태만 사용 (서비스는 단일 상태만 지원)
    searchTerm: filters.search,
    page: 1,
    limit: 50
  };
};

// Context 인터페이스
interface ApplicationContextValue {
  state: ApplicationState;
  actions: {
    fetchApplications: (hospitalId: string, filters?: ApplicationFilters) => Promise<void>;
    updateApplicationStatus: (id: string, status: ApplicationStatus, reason?: string) => Promise<void>;
    bulkUpdateStatus: (ids: string[], status: ApplicationStatus, reason?: string) => Promise<void>;
    deleteApplication: (id: string) => Promise<void>;
    setSelectedIds: (ids: string[]) => void;
    toggleSelectedId: (id: string) => void;
    clearSelectedIds: () => void;
    setFilters: (filters: ApplicationFilters) => void;
    clearFilters: () => void;
    refreshStatistics: (hospitalId: string) => Promise<void>;
    subscribeToSocketUpdates: () => void;
    unsubscribeFromSocketUpdates: () => void;
  };
}

// Context 생성
const ApplicationContext = createContext<ApplicationContextValue | undefined>(undefined);

// Provider 컴포넌트
export const ApplicationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(applicationReducer, initialState);
  const notificationContext = useNotificationContext();

  // 지원자 목록 조회
  const fetchApplications = useCallback(async (hospitalId: string, filters?: ApplicationFilters) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      // 매개변수로 받은 필터를 사용하거나 빈 객체를 기본값으로 사용
      const currentFilters = filters || {};
      const serviceFilters = convertFiltersForService(currentFilters);
      const response = await applicationService.getApplications(hospitalId, serviceFilters);
      dispatch({ type: 'SET_APPLICATIONS', payload: response.data.applications });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '지원자 목록을 불러오는데 실패했습니다.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      if (notificationContext) {
        // TODO: 알림 표시 (NotificationContext 인터페이스 확인 후 구현)
      }
    }
  }, [notificationContext]);

  // 지원자 상태 업데이트
  const updateApplicationStatus = useCallback(async (
    id: string, 
    status: ApplicationStatus, 
    reason?: string
  ) => {
    try {
      await applicationService.updateApplicationStatus(id, { 
        status, 
        rejectionReason: reason 
      });
      dispatch({ 
        type: 'UPDATE_APPLICATION', 
        payload: { id, updates: { status } } 
      });
      // 성공 알림은 필요시 추가
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '상태 변경에 실패했습니다.';
      throw new Error(errorMessage);
    }
  }, []);

  // 일괄 상태 업데이트
  const bulkUpdateStatus = useCallback(async (
    ids: string[], 
    status: ApplicationStatus, 
    reason?: string
  ) => {
    try {
      dispatch({ type: 'SET_BULK_ACTION_LOADING', payload: true });
      await applicationService.bulkUpdateApplications({
        applicationIds: ids,
        status,
        rejectionReason: reason
      });
      
      // 로컬 상태 업데이트
      ids.forEach(id => {
        dispatch({ 
          type: 'UPDATE_APPLICATION', 
          payload: { id, updates: { status } } 
        });
      });
      
      dispatch({ type: 'CLEAR_SELECTED_IDS' });
      // 성공 알림은 필요시 추가
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '일괄 처리에 실패했습니다.';
      throw new Error(errorMessage);
    } finally {
      dispatch({ type: 'SET_BULK_ACTION_LOADING', payload: false });
    }
  }, []);

  // 지원서 삭제
  const deleteApplication = useCallback(async (id: string) => {
    try {
      await applicationService.deleteApplication(id);
      dispatch({ type: 'REMOVE_APPLICATION', payload: id });
      // 성공 알림은 필요시 추가
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '지원서 삭제에 실패했습니다.';
      throw new Error(errorMessage);
    }
  }, []);

  // 선택 관련 액션들
  const setSelectedIds = useCallback((ids: string[]) => {
    dispatch({ type: 'SET_SELECTED_IDS', payload: ids });
  }, []);

  const toggleSelectedId = useCallback((id: string) => {
    dispatch({ type: 'TOGGLE_SELECTED_ID', payload: id });
  }, []);

  const clearSelectedIds = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTED_IDS' });
  }, []);

  // 필터 관련 액션들
  const setFilters = useCallback((filters: ApplicationFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_FILTERS' });
  }, []);

  // 통계 새로고침 (기본 구현 - 실제 API 연동은 추후)
  const refreshStatistics = useCallback(async (hospitalId: string) => {
    try {
      // 현재 상태의 applications를 함수 내부에서 참조 (안전장치 포함)
      const currentApplications = state.applications || [];
      const stats: ApplicationStats = {
        total: currentApplications.length,
        byStatus: {} as Record<ApplicationStatus, number>,
        recentActivity: 0,
        avgProcessingTime: 0,
        successRate: 0
      };

      // 상태별 카운트
      Object.values(ApplicationStatus).forEach(status => {
        stats.byStatus[status] = currentApplications.filter(app => app.status === status).length;
      });

      dispatch({ type: 'SET_STATISTICS', payload: stats });
    } catch (error) {
      console.error('Failed to refresh statistics:', error);
    }
  }, []); // state.applications 의존성 제거

  // Socket.io 이벤트 구독 (기본 구현)
  const subscribeToSocketUpdates = useCallback(() => {
    // 실제 소켓 연결은 추후 구현
    dispatch({ type: 'SET_SOCKET_CONNECTED', payload: true });
  }, []);

  // Socket.io 구독 해제
  const unsubscribeFromSocketUpdates = useCallback(() => {
    dispatch({ type: 'SET_SOCKET_CONNECTED', payload: false });
  }, []);

  // Context 값
  const contextValue: ApplicationContextValue = useMemo(() => ({
    state,
    actions: {
      fetchApplications,
      updateApplicationStatus,
      bulkUpdateStatus,
      deleteApplication,
      setSelectedIds,
      toggleSelectedId,
      clearSelectedIds,
      setFilters,
      clearFilters,
      refreshStatistics,
      subscribeToSocketUpdates,
      unsubscribeFromSocketUpdates
    }
  }), [
    state,
    fetchApplications,
    updateApplicationStatus,
    bulkUpdateStatus,
    deleteApplication,
    setSelectedIds,
    toggleSelectedId,
    clearSelectedIds,
    setFilters,
    clearFilters,
    refreshStatistics,
    subscribeToSocketUpdates,
    unsubscribeFromSocketUpdates
  ]);

  return (
    <ApplicationContext.Provider value={contextValue}>
      {children}
    </ApplicationContext.Provider>
  );
};

// Context Hook
export const useApplication = (): ApplicationContextValue => {
  const context = useContext(ApplicationContext);
  if (!context) {
    throw new Error('useApplication must be used within an ApplicationProvider');
  }
  
  // 추가 안전장치: state가 제대로 초기화되었는지 확인
  if (!context.state) {
    throw new Error('ApplicationContext state is not initialized');
  }
  
  // applications 배열이 undefined인 경우 빈 배열로 초기화
  if (!context.state.applications) {
    console.warn('ApplicationContext: applications is undefined, initializing as empty array');
    context.state.applications = [];
  }
  
  return context;
}; 